/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ObservableModel } from '@typescript-kit/core';

export class TimeRecordingSummaryKey {
  static readonly TOTAL_WORK_HOURS = 'totalWorkHours';
  static readonly TOTAL_PUBLIC_HOLIDAY_HOURS = 'totalPublicHolidayHours';
  static readonly TOTAL_SICK_HOURS = 'totalSickHours';
  static readonly TOTAL_SHORT_TIME_WORK_HOURS = 'totalShortTimeWorkHours';
  static readonly TOTAL_HOLIDAY_HOURS = 'totalHolidayHours';
  static readonly TOTAL_COMP_TIME_HOURS = 'totalCompTimeHours';
  static readonly TOTAL_ACTUAL_HOURS = 'totalActualHours';
  static readonly TOTAL_EXPECTED_HOURS = 'totalExpectedHours';
  static readonly TOTAL_SALARIED_HOURS = 'totalSalariedHours';
  static readonly TOTAL_OVERTIME = 'totalOvertime';
  static readonly TOTAL_OVERTIME_50 = 'totalOvertime50';
  static readonly TOTAL_OVERTIME_100 = 'totalOvertime100';
  static readonly TOTAL_HOURS = 'totalHours';
  static readonly TOTAL_FLEXTIME = 'totalFlextime';
  static readonly FLEXTIME_CARRYOVER = 'flextimeCarryover';
  static readonly FLEXTIME_BALANCE = 'flextimeBalance';
  static readonly HOLIDAY_CARRYOVER = 'holidayCarryover';
  static readonly HOLIDAY_BALANCE = 'holidayBalance';
}

export interface TimeRecordingSummaryData {
  totalWorkHours?: number;
  totalPublicHolidayHours?: number;
  totalSickHours?: number;
  totalShortTimeWorkHours?: number;
  totalHolidayHours?: number;
  totalCompTimeHours?: number;
  totalActualHours?: number;
  totalExpectedHours?: number;
  totalSalariedHours?: number;
  totalOvertime?: number;
  totalOvertime50?: number;
  totalOvertime100?: number;
  totalHours?: number;
  totalFlextime?: number;
  flextimeCarryover: number;
  flextimeBalance: number;
  holidayCarryover: number;
  holidayBalance: number;
}

export class TimeRecordingSummary implements TimeRecordingSummaryData {
  readonly totalWorkHours: number;
  readonly totalPublicHolidayHours: number;
  readonly totalSickHours: number;
  readonly totalShortTimeWorkHours: number;
  readonly totalHolidayHours: number;
  readonly totalCompTimeHours: number;
  readonly totalActualHours: number;
  readonly totalExpectedHours: number;
  readonly totalSalariedHours: number;
  readonly totalOvertime: number;
  readonly totalOvertime50: number;
  readonly totalOvertime100: number;
  readonly totalHours: number;
  readonly totalFlextime: number;
  readonly flextimeCarryover: number;
  readonly flextimeBalance: number;
  readonly holidayCarryover: number;
  readonly holidayBalance: number;

  constructor(data: TimeRecordingSummaryData) {
    this.totalWorkHours = data.totalWorkHours;
    this.totalPublicHolidayHours = data.totalPublicHolidayHours;
    this.totalSickHours = data.totalSickHours;
    this.totalShortTimeWorkHours = data.totalShortTimeWorkHours;
    this.totalHolidayHours = data.totalHolidayHours;
    this.totalCompTimeHours = data.totalCompTimeHours;
    this.totalActualHours = data.totalActualHours;
    this.totalExpectedHours = data.totalExpectedHours;
    this.totalSalariedHours = data.totalSalariedHours;
    this.totalOvertime = data.totalOvertime;
    this.totalOvertime50 = data.totalOvertime50;
    this.totalOvertime100 = data.totalOvertime100;
    this.totalHours = data.totalHours;
    this.totalFlextime = data.totalFlextime;
    this.flextimeCarryover = data.flextimeCarryover;
    this.flextimeBalance = data.flextimeBalance;
    this.holidayCarryover = data.holidayCarryover;
    this.holidayBalance = data.holidayBalance;
  }
}

export class TimeRecordingSummaryModel extends ObservableModel implements TimeRecordingSummaryData {
  static readonly KEYS = Object.keys(TimeRecordingSummaryKey).map((key) => TimeRecordingSummaryKey[key]);

  constructor(data: TimeRecordingSummaryData) {
    super(data, TimeRecordingSummaryModel.KEYS);
  }

  get totalWorkHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_WORK_HOURS);
  }

  set totalWorkHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_WORK_HOURS, value);
  }

  get totalPublicHolidayHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_PUBLIC_HOLIDAY_HOURS);
  }

  set totalPublicHolidayHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_PUBLIC_HOLIDAY_HOURS, value);
  }

  get totalSickHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_SICK_HOURS);
  }

  set totalSickHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_SICK_HOURS, value);
  }

  get totalShortTimeWorkHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_SHORT_TIME_WORK_HOURS);
  }

  set totalShortTimeWorkHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_SHORT_TIME_WORK_HOURS, value);
  }

  get totalHolidayHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_HOLIDAY_HOURS);
  }

  set totalHolidayHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_HOLIDAY_HOURS, value);
  }

  get totalCompTimeHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_COMP_TIME_HOURS);
  }

  set totalCompTimeHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_COMP_TIME_HOURS, value);
  }

  get totalActualHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_ACTUAL_HOURS);
  }

  set totalActualHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_ACTUAL_HOURS, value);
  }

  get totalExpectedHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_EXPECTED_HOURS);
  }

  set totalExpectedHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_EXPECTED_HOURS, value);
  }

  get totalSalariedHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_SALARIED_HOURS);
  }

  set totalSalariedHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_SALARIED_HOURS, value);
  }

  get totalOvertime(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_OVERTIME);
  }

  set totalOvertime(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_OVERTIME, value);
  }

  get totalOvertime50(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_OVERTIME_50);
  }

  set totalOvertime50(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_OVERTIME_50, value);
  }

  get totalOvertime100(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_OVERTIME_100);
  }

  set totalOvertime100(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_OVERTIME_100, value);
  }

  get totalHours(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_HOURS);
  }

  set totalHours(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_HOURS, value);
  }

  get totalFlextime(): number {
    return this.get(TimeRecordingSummaryKey.TOTAL_FLEXTIME);
  }

  set totalFlextime(value: number) {
    this.set(TimeRecordingSummaryKey.TOTAL_FLEXTIME, value);
  }

  get flextimeCarryover(): number {
    return this.get(TimeRecordingSummaryKey.FLEXTIME_CARRYOVER);
  }

  set flextimeCarryover(value: number) {
    this.set(TimeRecordingSummaryKey.FLEXTIME_CARRYOVER, value);
  }

  get flextimeBalance(): number {
    return this.get(TimeRecordingSummaryKey.FLEXTIME_BALANCE);
  }

  set flextimeBalance(value: number) {
    this.set(TimeRecordingSummaryKey.FLEXTIME_BALANCE, value);
  }

  get holidayCarryover(): number {
    return this.get(TimeRecordingSummaryKey.HOLIDAY_CARRYOVER);
  }

  set holidayCarryover(value: number) {
    this.set(TimeRecordingSummaryKey.HOLIDAY_CARRYOVER, value);
  }

  get holidayBalance(): number {
    return this.get(TimeRecordingSummaryKey.HOLIDAY_BALANCE);
  }

  set holidayBalance(value: number) {
    this.set(TimeRecordingSummaryKey.HOLIDAY_BALANCE, value);
  }

}
